import type { Timestamp } from 'firebase/firestore'

export type UserRole = 'care_recipient' | 'caregiver' | 'admin'

/**
 * A user as the admin panel shows it. Built by `toUser()` (lib/normalize.ts) from the
 * Firestore document, which may use the camelCase fields the apps and sign-up write
 * or the older snake_case ones — pages never read raw user documents.
 */
export interface User {
  id: string
  email: string
  /** Best available full name: first + last, else name / displayName / display_name. */
  displayName: string
  firstName?: string
  lastName?: string
  role: UserRole
  businessId: string
  subscriptionTier?: string
  /** False only when the account has been disabled. */
  isActive: boolean
  requiresPasswordChange: boolean
  createdAt?: Timestamp
  lastLogin?: Timestamp
}

/** A care circle as the admin panel shows it; built by `toBusiness()`. */
export interface Business {
  id: string
  name: string
  ownerId?: string
  subscription?: {
    tier?: string
    status?: 'trialing' | 'active' | 'past_due' | 'canceled'
    billing_interval?: 'monthly' | 'yearly'
  }
  usage_stats?: {
    care_recipients_count: number
    caregivers_count: number
    documents_count: number
    photos_count: number
    medications_count: number
    storage_used_mb: number
  }
  createdAt?: Timestamp
}

export interface SubscriptionPackage {
  id: string
  tier: 'free' | 'family' | 'care_pro' | 'enterprise'
  name: string
  description: string
  monthly_price_eur: number
  yearly_price_eur: number
  max_caregivers: number | null
  features: Record<string, { enabled: boolean; limit: number | null }>
  is_active: boolean
  display_order: number
  created_at: Timestamp
  updated_at: Timestamp
}

export interface UsageEvent {
  id: string
  user_id: string
  business_id: string
  event_type: 'feature_used' | 'login' | 'call_started' | 'message_sent'
  feature_name?: string
  metadata?: Record<string, string>
  timestamp: Timestamp
}

export interface UsageAnalytics {
  id: string
  business_id: string
  date: string
  total_users: number
  active_users: number
  total_calls: number
  total_messages: number
  feature_usage: Record<string, number>
  created_at: Timestamp
}

/**
 * Feature keys shared by every platform (CROSS_PLATFORM_CONTRACTS.md §5) — the same
 * list as the iOS `AppFeature` enum. A key missing from a saved list is OFF; a
 * missing document or field means every feature is ON.
 */
export const APP_FEATURE_KEYS = [
  'voice_assistant',
  'emergency_sos',
  'check_in',
  'reminders',
  'calendar',
  'medications',
  'shopping_list',
  'document_scanner',
  'document_library',
  'photo_album',
  'location_sharing',
  'volume_control',
  'battery_monitor',
] as const

export type AppFeatureKey = (typeof APP_FEATURE_KEYS)[number]

// Display label/description live in the i18n catalogs under documents.collections.<name>.
export interface CollectionMetadata {
  name: string
  hasAuth: boolean
  /** Age field; alternatives are queried in turn (camelCase first, legacy snake_case after). */
  timestampFields: string[]
  /** Columns to preview. Each lists the field's spellings; the first one present is shown. */
  displayFields: DisplayField[]
  canBulkDelete: boolean
}

export interface DisplayField {
  /** Column header — a technical field name, deliberately not translated. */
  label: string
  keys: string[]
}

export interface BulkDeleteFilter {
  collection: string
  olderThanDays: number
  excludeActive: boolean
  maxDocuments: number
}

export interface BulkDeleteResult {
  success: boolean
  deletedCount: number
  failedCount: number
  errors: Array<{ id: string; error: string }>
}

export interface DocumentPreview {
  id: string
  [key: string]: unknown
}
